import { CheckCircle2, ExternalLink, GitBranch, PauseCircle } from "lucide-react";
import { useGitHubStatus, useRemoveGitHubInstallation } from "@/hooks/queries/use-github";
import { Button } from "@/components/ui/button";

/**
 * Settings → GitHub: connect the company's GitHub through the CompBox GitHub
 * App. An organization admin installs it once on their GitHub organization and
 * picks which repositories CompBox may use; everyone else sees the connection.
 */
export function GitHubCard() {
  const { data: status, isLoading } = useGitHubStatus();
  const remove = useRemoveGitHubInstallation();

  if (isLoading || !status) return <div className="h-16 animate-pulse rounded-lg bg-muted" />;

  if (!status.configured) {
    return <p className="text-sm text-muted-foreground">Connecting GitHub isn't available on this CompBox server yet.</p>;
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        The CompBox app on GitHub lets CompBox pick and create repositories for your projects and open pull requests,
        using short-lived access to only the repositories you choose. No passwords or tokens are stored.
      </p>

      {status.installations.length > 0 ? (
        <ul className="space-y-2">
          {status.installations.map((i) => (
            <li
              key={i.installationId}
              className="flex flex-col gap-2 rounded-lg border border-white/[0.08] p-3 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="flex min-w-0 items-center gap-2 text-sm">
                {i.suspended ? (
                  <PauseCircle className="h-4 w-4 shrink-0 text-amber-400" />
                ) : (
                  <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-400" />
                )}
                <span className="font-medium">{i.accountLogin}</span>
                <span className="text-xs text-muted-foreground">
                  {i.suspended
                    ? "suspended on GitHub"
                    : i.repositorySelection === "all"
                      ? "all repositories"
                      : "selected repositories"}
                </span>
              </div>
              {status.canManage ? (
                <div className="flex shrink-0 gap-2">
                  <Button asChild size="sm" variant="outline">
                    <a href={i.manageUrl} target="_blank" rel="noreferrer">
                      <ExternalLink className="mr-1 h-3.5 w-3.5" />
                      Choose repositories
                    </a>
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={remove.isPending}
                    onClick={() => { remove.mutate(i.installationId); }}
                  >
                    Disconnect
                  </Button>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}

      {status.canManage && status.installUrl ? (
        <Button asChild size="sm" variant={status.installations.length > 0 ? "outline" : "default"}>
          <a href={status.installUrl}>
            <GitBranch className="mr-2 h-4 w-4" />
            {status.installations.length > 0 ? "Connect another GitHub organization" : "Connect GitHub"}
          </a>
        </Button>
      ) : status.installations.length === 0 ? (
        <p className="text-sm text-muted-foreground">Not connected yet. Ask an organization admin to connect GitHub.</p>
      ) : null}
    </div>
  );
}
